import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
    getSingleAssignment,
    addJobNote,
    updateJobNote,
    deleteJobNote,
    getAvailableDesigners,
    getAvailableEstimators,
    getAvailableDrivers,
    assignDesigner,
    removeDesigner,
    assignEstimator,
    removeEstimator,
    assignDriver,
    removeDriver,
    confirmPackageReceipt,
    uploadDeliveryPhoto,
    completeDelivery,
    completeDesign,
    completeEstimation,
    subscribeToJobUpdates
} from "../../lib/supabase/single-assignment";

import {
    FiArrowLeft, FiEdit3, FiDownload, FiCalendar, FiUser, FiPhone,
    FiMapPin, FiCheckCircle, FiClock, FiAlertCircle, FiFileText,
    FiHome, FiLayers, FiTool, FiDollarSign, FiFlag, FiUpload,
    FiCheck, FiX, FiEye, FiMail, FiTruck,
    FiPenTool
} from "react-icons/fi";
import DragDropUploader from "../common/DragDropUploader";
import FileViewer from '../common/FileViewer';
import { hasPermission } from "../../lib/permissions";
import { sendInTransitNotificationSMS } from "../../lib/supabase/sms";

const SingleAssignment = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug, jobId } = useParams();

    const [assignment, setAssignment] = useState(null);
    const [actionLogs, setActionLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteContent, setEditingNoteContent] = useState("");

    const [showDesignerModal, setShowDesignerModal] = useState(false);
    const [showEstimatorModal, setShowEstimatorModal] = useState(false);
    const [selectedQuoteType, setSelectedQuoteType] = useState(null);
    const [selectedDesignerId, setSelectedDesignerId] = useState('');
    const [selectedEstimatorId, setSelectedEstimatorId] = useState('');
    const [availableDesigners, setAvailableDesigners] = useState([]);
    const [availableEstimators, setAvailableEstimators] = useState([]);

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isSubmittingCompletion, setIsSubmittingCompletion] = useState(false);
    const [clearPdfs, setClearPdfs] = useState(false);
    const [completionNotes, setCompletionNotes] = useState({
        roof: '',
        wall: '',
        floor: '',
        estimation: ''
    });

    // Driver delivery states
    const [uploadedDeliveryPhotos, setUploadedDeliveryPhotos] = useState({
        roof: [],
        wall: [],
        floor: [],
        timber: []
    });
    const [clearDeliveryPhotos, setClearDeliveryPhotos] = useState({
        roof: false,
        wall: false,
        floor: false,
        timber: false
    });
    const [deliveryNotes, setDeliveryNotes] = useState({
        roof: '',
        wall: '',
        floor: '',
        timber: '',
    });
    const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);

    const [showDriverModal, setShowDriverModal] = useState(false);
    const [selectedQuoteTypeForDriver, setSelectedQuoteTypeForDriver] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [availableDrivers, setAvailableDrivers] = useState([]);

    // File viewer states
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [viewerFiles, setViewerFiles] = useState([]);
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);

    useEffect(() => {
        if (company?.id && jobId) {
            fetchAssignment();
        }
    }, [company, jobId]);

    const fetchAssignment = async () => {
        try {
            const data = await getSingleAssignment(jobId, company.id);
            setAssignment(data);

            const sortedLogs = (data.job_action_logs || []).sort((a, b) =>
                new Date(b.timestamp) - new Date(a.timestamp)
            );
            setActionLogs(sortedLogs);
        } catch (error) {
            console.error("Error fetching assignment:", error);
            alert("Error fetching assignment details");
            navigate(`/${companySlug}/assignments`);
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!assignment?.id) return;

        const channel = subscribeToJobUpdates(assignment.id, (payload) => {
            fetchAssignment();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [assignment?.id]);

    // Keyboard navigation for file viewer
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!fileViewerOpen) return;

            if (e.key === 'Escape') {
                setFileViewerOpen(false);
            } else if (e.key === 'ArrowLeft' && selectedFileIndex > 0) {
                setSelectedFileIndex(prev => prev - 1);
                setSelectedFile(viewerFiles[selectedFileIndex - 1]);
            } else if (e.key === 'ArrowRight' && selectedFileIndex < viewerFiles.length - 1) {
                setSelectedFileIndex(prev => prev + 1);
                setSelectedFile(viewerFiles[selectedFileIndex + 1]);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [fileViewerOpen, selectedFileIndex, viewerFiles]);

    const canUserModifyNote = (note) => {
        return note.added_by_user_id === userProfile?.id || userProfile?.role === 'admin';
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;

        try {
            await addJobNote(
                assignment.id,
                newNote.trim(),
                userProfile.id,
                userProfile.username || userProfile.full_name,
                userProfile.role
            );
            setNewNote("");
            fetchAssignment();
        } catch (error) {
            console.error("Error adding note:", error);
            alert("Error adding note");
        }
    };

    const startEditNote = (noteId, content) => {
        setEditingNoteId(noteId);
        setEditingNoteContent(content);
    };

    const cancelEditNote = () => {
        setEditingNoteId(null);
        setEditingNoteContent("");
    };

    const handleSaveEditNote = async (noteId) => {
        if (!editingNoteContent.trim()) return;

        try {
            await updateJobNote(noteId, editingNoteContent.trim(), userProfile.id);
            cancelEditNote();
            fetchAssignment();
        } catch (error) {
            console.error("Error updating note:", error);
            alert(error.message || "Error updating note");
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm("Are you sure you want to delete this note? This action cannot be undone.")) {
            return;
        }

        try {
            await deleteJobNote(noteId, userProfile.id);
            fetchAssignment();
        } catch (error) {
            console.error("Error deleting note:", error);
            alert(error.message || "Error deleting note");
        }
    };

    const openAssignDesignerModal = async (quoteType) => {
        setSelectedQuoteType(quoteType);
        try {
            const designers = await getAvailableDesigners(company.id);
            setAvailableDesigners(designers);
            setShowDesignerModal(true);
        } catch (error) {
            console.error("Error fetching designers:", error);
            alert("Error loading designers");
        }
    };

    const openAssignEstimatorModal = async () => {
        try {
            const estimators = await getAvailableEstimators(company.id);
            setAvailableEstimators(estimators);
            setShowEstimatorModal(true);
        } catch (error) {
            console.error("Error fetching estimators:", error);
            alert("Error loading estimators");
        }
    };

    const openAssignDriverModal = async (quoteType) => {
        setSelectedQuoteTypeForDriver(quoteType);
        try {
            const drivers = await getAvailableDrivers(company.id);
            setAvailableDrivers(drivers);
            setShowDriverModal(true);
        } catch (error) {
            console.error("Error fetching drivers:", error);
            alert("Error loading drivers");
        }
    };

    const handleAssignDriver = async () => {
        if (!selectedDriverId || !selectedQuoteTypeForDriver) return;

        try {
            // Parse the selected driver ID (format: "id_name")
            const [driverId, driverName] = selectedDriverId.split('_');

            await assignDriver(assignment.id, selectedQuoteTypeForDriver, driverId, driverName);

            setShowDriverModal(false);
            setSelectedDriverId('');
            setSelectedQuoteTypeForDriver(null);
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning driver:", error);
            alert(error.message || "Error assigning driver");
        }
    };

    const handleRemoveDriver = async (quoteType) => {
        if (!window.confirm("Are you sure you want to remove this driver?")) return;

        try {
            await removeDriver(assignment.id, quoteType);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing driver:", error);
            alert(error.message || "Error removing driver");
        }
    };

    const handleConfirmPackage = async (quoteType) => {
        try {
            await confirmPackageReceipt(assignment.id, quoteType);
            // Get the expected delivery date for this specific quote
            const quote = assignment?.job_quotes?.find(q => q.quote_type === quoteType);
            const deliveryDate = quote?.expected_delivery_date || null;
            await sendInTransitNotificationSMS(assignment?.customer_number, assignment?.customer_name, assignment?.short_id, assignment?.id, deliveryDate, company) // phoneNumber, customerName, shortId, jobId, deliveryDate, company
            fetchAssignment();
        } catch (error) {
            console.error("Error confirming package:", error);
            alert(error.message || "Error confirming package receipt");
        }
    };

    const handleEndDelivery = async (quoteType) => {
        if (!window.confirm("Are you sure you want to mark this delivery as completed?")) return;

        try {
            await completeDelivery(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                deliveryNotes[quoteType] || ''
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error completing delivery:", error);
            alert(error.message || "Error completing delivery");
        }
    };

    const handleUploadDeliveryPhotos = async (quoteType) => {
        if (!uploadedDeliveryPhotos[quoteType] || uploadedDeliveryPhotos[quoteType].length === 0) {
            alert("Please select at least one photo to upload");
            return;
        }

        setIsSubmittingDelivery(true);

        try {
            const photoFiles = uploadedDeliveryPhotos[quoteType];
            const uploadedFileRecords = [];

            // Upload each photo
            for (const file of photoFiles) {
                const fileRecord = await uploadDeliveryPhoto(
                    assignment.id,
                    file,
                    quoteType,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug
                );
                uploadedFileRecords.push(fileRecord);
            }

            // Update delivery notes if provided
            if (deliveryNotes[quoteType]?.trim()) {
                const { error: notesError } = await supabase
                    .from('job_deliveries')
                    .update({
                        delivery_notes: deliveryNotes[quoteType].trim(),
                        note_updated_at: new Date().toISOString(),
                        note_updated_by_user_id: userProfile.id,
                        note_updated_by_name: userProfile.username || userProfile.full_name
                    })
                    .eq('job_id', assignment.id)
                    .eq('quote_type', quoteType);

                if (notesError) throw notesError;
            }

            // Clear the upload state
            setUploadedDeliveryPhotos(prev => ({
                ...prev,
                [quoteType]: []
            }));

            setClearDeliveryPhotos(prev => ({
                ...prev,
                [quoteType]: true
            }));

            setTimeout(() => {
                setClearDeliveryPhotos(prev => ({
                    ...prev,
                    [quoteType]: false
                }));
            }, 100);

            alert(`Successfully uploaded ${uploadedFileRecords.length} photo(s)`);
            fetchAssignment();
        } catch (error) {
            console.error("Error uploading delivery photos:", error);
            alert(error.message || "Error uploading delivery photos");
        } finally {
            setIsSubmittingDelivery(false);
        }
    };

    const handleDeliveryPhotosChange = (quoteType) => (files) => {
        setUploadedDeliveryPhotos(prev => ({
            ...prev,
            [quoteType]: files
        }));
    };

    const handleAssignDesigner = async () => {
        if (!selectedDesignerId || !selectedQuoteType) return;

        try {
            const designer = availableDesigners.find(d => d.id === selectedDesignerId);
            await assignDesigner(
                assignment.id,
                selectedQuoteType,
                selectedDesignerId,
                designer.username || designer.full_name
            );

            setShowDesignerModal(false);
            setSelectedDesignerId('');
            setSelectedQuoteType(null);
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning designer:", error);
            alert(error.message || "Error assigning designer");
        }
    };

    const handleSelfAssignDesigner = async (quoteType) => {
        try {
            await assignDesigner(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error self-assigning:", error);
            alert(error.message || "Error assigning yourself");
        }
    };

    const handleRemoveDesigner = async (quoteType, designerId) => {
        if (!window.confirm("Are you sure you want to remove this designer?")) return;

        try {
            await removeDesigner(assignment.id, quoteType, designerId);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing designer:", error);
            alert(error.message || "Error removing designer");
        }
    };

    const handleAssignEstimator = async () => {
        if (!selectedEstimatorId) return;

        try {
            const estimator = availableEstimators.find(e => e.id === selectedEstimatorId);
            await assignEstimator(
                assignment.id,
                selectedEstimatorId,
                estimator.username || estimator.full_name,
            );

            setShowEstimatorModal(false);
            setSelectedEstimatorId('');
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning estimator:", error);
            alert(error.message || "Error assigning estimator");
        }
    };

    const handleSelfAssignEstimator = async () => {
        try {
            await assignEstimator(
                assignment.id,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error self-assigning estimator:", error);
            alert(error.message || "Error assigning yourself as estimator");
        }
    };

    const handleRemoveEstimator = async () => {
        if (!window.confirm("Are you sure you want to remove the estimator?")) return;

        try {
            await removeEstimator(assignment.id);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing estimator:", error);
            alert(error.message || "Error removing estimator");
        }
    };

    const handleCompleteDesign = async (quoteType) => {
        if (!quoteType || !uploadedFiles?.pdfFiles || uploadedFiles.pdfFiles.length === 0) {
            alert("Please upload at least one file before completing the design");
            return;
        }

        if (!window.confirm(`Are you sure you want to complete the ${quoteType} design? This action cannot be undone.`)) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            const result = await completeDesign(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                completionNotes[quoteType] || '',
                uploadedFiles.pdfFiles,
                companySlug
            );

            // Success feedback
            const uploadCount = result.uploaded_files?.length || 0;
            alert(`Design completed successfully! ${uploadCount} file(s) uploaded.`);

            // Reset form
            setUploadedFiles({ pdfFiles: [] });
            setCompletionNotes(prev => ({
                ...prev,
                [quoteType]: ''
            }));
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            // Refresh assignment data
            fetchAssignment();
        } catch (error) {
            console.error("Error completing design:", error);
            alert(error.message || "Error completing design. Please try again.");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handleCompleteEstimation = async () => {
        if (!uploadedFiles?.pdfFiles || uploadedFiles.pdfFiles.length === 0) {
            alert("Please upload at least one file before completing the estimation");
            return;
        }

        if (!window.confirm("Are you sure you want to complete the estimation? This action cannot be undone.")) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            const result = await completeEstimation(
                assignment.id,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                completionNotes.estimation || '',
                uploadedFiles.pdfFiles,
                companySlug
            );

            // Success feedback
            const uploadCount = result.uploaded_files?.length || 0;
            alert(`Estimation completed successfully! ${uploadCount} file(s) uploaded.`);

            // Reset form
            setUploadedFiles({ pdfFiles: [] });
            setCompletionNotes(prev => ({
                ...prev,
                estimation: ''
            }));
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            // Refresh assignment data
            fetchAssignment();
        } catch (error) {
            console.error("Error completing estimation:", error);
            alert(error.message || "Error completing estimation. Please try again.");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handlePdfsChange = (pdfFiles) => {
        setUploadedFiles(prevState => ({ ...prevState, pdfFiles }));
    };

    const getStatusColor = (status) => {
        const statusColors = {
            pending_design: "bg-yellow-100 text-yellow-800 border-yellow-200",
            in_design: "bg-blue-100 text-blue-800 border-blue-200",
            design_completed: "bg-green-100 text-green-800 border-green-200",
            pending_estimation: "bg-orange-100 text-orange-800 border-orange-200",
            in_estimation: "bg-purple-100 text-purple-800 border-purple-200",
            estimation_completed: "bg-green-100 text-green-800 border-green-200",
            ready_for_delivery: "bg-emerald-100 text-emerald-800 border-emerald-200",
            completed: "bg-gray-100 text-gray-800 border-gray-200"
        };
        return statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200";
    };

    const getRequiredSections = (job) => {
        const sections = [];
        job.job_quotes?.forEach(quote => {
            if (quote.is_required) {
                sections.push(quote.quote_type.charAt(0).toUpperCase() + quote.quote_type.slice(1));
            }
        });
        return sections;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const downloadFile = async (file) => {
        try {
            // The file.filename already contains the full path: companySlug/jobId/type-timestamp-filename
            const filePath = `${companySlug}/${assignment.id}/${file.filename}`

            const { data, error } = await supabase.storage
                .from('job-files')
                .download(filePath);

            if (error) {
                console.error('Download error:', error);
                throw error;
            }

            // Create a download link
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.original_name || filePath.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file: ' + (err.message || 'Unknown error'));
        }
    };

    const seeFile = (file, allFiles = []) => {
        setSelectedFile(file);
        setViewerFiles(allFiles.length > 0 ? allFiles : [file]);
        setSelectedFileIndex(allFiles.findIndex(f => f.filename === file.filename));
        setFileViewerOpen(true);
    };

    const getDriverAssignedQuotes = () => {
        if (userProfile?.role !== 'driver') return [];

        const assignedQuotes = [];
        assignment.job_deliveries?.forEach(delivery => {
            if (delivery.assigned_driver_id === userProfile.id) {
                assignedQuotes.push(delivery.quote_type);
            }
        });
        return assignedQuotes;
    };

    const canDesignerCompleteQuote = (quoteType) => {
        if (userProfile?.role !== 'designer') return false;

        const userDesigner = assignment.job_designers?.find(
            d => d.quote_type === quoteType && d.user_id === userProfile.id
        );
        return userDesigner && !userDesigner.is_completed;
    };

    // Status Badge Component
    const StatusBadge = ({ status, large = false }) => {
        const isTimber = assignment?.job_type === "Timber";
        const displayStatus = isTimber
            ? (status === 'ready_for_delivery' ? 'Ready for Delivery' :
                status === 'completed' ? 'Completed' : status.replace('_', ' '))
            : status.replace('_', ' ');

        return (
            <span className={`px-3 py-1 rounded-full font-medium border ${large ? 'text-sm' : 'text-xs'} ${getStatusColor(status)}`}>
                {displayStatus.toUpperCase()}
            </span>
        );
    };

    // Info Card Component
    const InfoCard = ({ title, children, icon: Icon, color = "blue" }) => (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
                <Icon className={`w-5 h-5 text-${color}-500`} />
                <h3 className="font-semibold text-gray-900">{title}</h3>
            </div>
            <div className="space-y-2">
                {children}
            </div>
        </div>
    );

    // Detail Row Component
    const DetailRow = ({ label, value, icon: Icon, link }) => (
        <div className="flex items-start gap-3">
            {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5" />}
            <div className="flex-1 min-w-0 mb-2">
                <p className="text-sm text-gray-600">{label}</p>
                {link ? (
                    <a href={link} className="text-sm font-medium text-gray-900 break-words hover:text-blue-600">
                        {value || "Not specified"}
                    </a>
                ) : (
                    <div className="text-sm font-medium text-gray-900 break-words">{value || "Not specified"}</div>
                )}
            </div>
        </div>
    );

    // Checkbox Display Component
    const CheckboxDisplay = ({ label, isChecked }) => (
        <div className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isChecked ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                {isChecked && <FiCheck className="w-3 h-3 text-white" />}
            </div>
            <span className={`text-sm ${isChecked ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                {label}
            </span>
        </div>
    );


    if (!assignment) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading assignment details...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="">
                <div className="space-y-8">
                    {/* Header */}
                    <div className="flex items-start justify-between border-b border-gray-200 pb-6">
                        <div className="flex items-start gap-4">
                            <button
                                onClick={() => navigate(`/${companySlug}/assignments`)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FiArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                    {assignment.unique_id} <span className="text-sm font-medium ml-2 opacity-70">#{assignment?.short_id}</span>
                                </h1>
                                <div className="flex flex-wrap items-center gap-2">
                                    <StatusBadge status={assignment.main_status} large />
                                    {(assignment.urgent_quote || assignment.urgent_design) && (
                                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium border border-red-200">
                                            URGENT
                                        </span>
                                    )}
                                    <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                                        {assignment.job_type}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => navigate(`/${companySlug}/assignment/${jobId}/edit`)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                                <FiEdit3 className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Main Content - Customer, Supervisor, etc. */}
                    <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                        {/* Customer Information */}
                        <InfoCard title="Customer Information" icon={FiUser} color="blue">
                            <DetailRow label="Customer Name" value={assignment.customer_name} icon={FiUser} />
                            <DetailRow
                                label="Phone Number"
                                value={assignment.customer_number}
                                icon={FiPhone}
                                link={assignment.customer_number && `tel:${assignment.customer_number}`}
                            />
                            <DetailRow
                                label="Email Address"
                                value={assignment.customer_mail}
                                icon={FiMail}
                                link={assignment.customer_mail && `mailto:${assignment.customer_mail}`}
                            />
                        </InfoCard>

                        {/* Supervisor Information */}
                        <InfoCard title="Supervisor Information" icon={FiTool} color="green">
                            <DetailRow label="Supervisor Name" value={assignment.carpenter_supervisor_name} icon={FiUser} />
                            <DetailRow
                                label="Phone Number"
                                value={assignment.carpenter_supervisor_number}
                                icon={FiPhone}
                                link={assignment.carpenter_supervisor_number && `tel:${assignment.carpenter_supervisor_number}`}
                            />
                        </InfoCard>

                        {/* Job Requirements */}
                        <InfoCard title="Job Requirements" icon={FiLayers} color="purple">
                            <DetailRow
                                label="Required Sections"
                                value={getRequiredSections(assignment).join(", ")}
                                icon={FiCheckCircle}
                            />
                            <DetailRow label="Job Type" value={assignment.job_type} icon={FiTool} />
                            {assignment.job_type !== "Timber" && (
                                <DetailRow
                                    label="Priority Settings"
                                    value={(
                                        <div className="flex flex-col gap-2 mt-2">
                                            <CheckboxDisplay label="Urgent Quote" isChecked={assignment.urgent_quote} />
                                            <CheckboxDisplay label="Urgent Design" isChecked={assignment.urgent_design} />
                                        </div>
                                    )}
                                    icon={FiClock}
                                />
                            )}
                        </InfoCard>

                        {/* Timeline */}
                        <InfoCard title="Timeline" icon={FiCalendar} color="orange">
                            <DetailRow label="Created" value={formatDate(assignment.created_at)} icon={FiCalendar} />
                            <DetailRow label="Last Updated" value={formatDate(assignment.updated_at)} icon={FiClock} />
                            <DetailRow label="Estimation Deadline" value={formatDate(assignment.expected_estimation_finish_date)} icon={FiFlag} />
                        </InfoCard>
                    </div>

                    {/* Driver Delivery Section - Only for assigned driver, grouped by quote type */}
                    {hasPermission(userProfile?.role, ["driver"]) && getDriverAssignedQuotes().length > 0 && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-teal-500 pb-2">
                                    My Deliveries
                                </h2>
                                <span className="text-sm text-gray-500">
                                    {getDriverAssignedQuotes().length} delivery assignment{getDriverAssignedQuotes().length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div className="space-y-6">
                                {getDriverAssignedQuotes().map(quoteType => {
                                    const delivery = assignment.job_deliveries?.find(d => d.quote_type === quoteType);
                                    const quote = assignment.job_quotes?.find(q => q.quote_type === quoteType);
                                    const deliveryPhotos = assignment.job_files?.filter(f =>
                                        f.file_type === 'delivery' && f.quote_type === quoteType
                                    ) || [];

                                    const statusConfig = {
                                        'not_scheduled': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' },
                                        'scheduled': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
                                        'in_transit': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
                                        'delivered': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' }
                                    };

                                    const config = statusConfig[delivery?.delivery_status] || statusConfig['not_scheduled'];

                                    return (
                                        <div key={quoteType} className={`rounded-xl border-2 ${config.border} ${config.bg} overflow-hidden`}>
                                            {/* Header */}
                                            <div className="px-6 py-4 bg-white border-b border-gray-100">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${quoteType === 'roof' ? 'bg-blue-100 text-blue-600' :
                                                            quoteType === 'wall' ? 'bg-red-100 text-red-600' :
                                                                quoteType === 'timber' ? 'bg-amber-100 text-amber-600' :
                                                                    'bg-green-100 text-green-600'
                                                            }`}>
                                                            {quoteType === 'roof' ? <FiHome className="w-4 h-4" /> :
                                                                quoteType === 'wall' ? <FiLayers className="w-4 h-4" /> :
                                                                    <FiLayers className="w-4 h-4 rotate-90" />}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-lg font-semibold text-gray-900 capitalize">
                                                                {quoteType} Delivery
                                                            </h3>
                                                            <p className="text-sm text-gray-500">
                                                                {delivery?.delivery_status?.replace('_', ' ').toUpperCase() || 'NOT SCHEDULED'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${delivery?.delivery_status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                        delivery?.delivery_status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                                                            delivery?.delivery_status === 'scheduled' ? 'bg-amber-100 text-amber-800' :
                                                                'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {delivery?.delivery_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-6 space-y-6">
                                                {/* Delivery Details */}
                                                <div className="grid gap-4 md:grid-cols-2">
                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiCalendar className="w-4 h-4 text-teal-600" />
                                                            <h4 className="font-semibold text-gray-800">Schedule</h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <DetailRow
                                                                label="Delivery Date"
                                                                value={formatDate(delivery?.delivery_date || quote?.expected_delivery_date)}
                                                            />
                                                            <DetailRow
                                                                label="Delivery Time"
                                                                value={delivery?.delivery_time || "Not specified"}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiUser className="w-4 h-4 text-teal-600" />
                                                            <h4 className="font-semibold text-gray-800">Contact</h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <DetailRow
                                                                label="Contact Name"
                                                                value={delivery?.delivery_contact_name || assignment.customer_name}
                                                            />
                                                            <DetailRow
                                                                label="Phone"
                                                                value={delivery?.delivery_contact_phone || assignment.customer_number}
                                                                link={`tel:${delivery?.delivery_contact_phone || assignment.customer_number}`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Delivery Address */}
                                                {delivery?.delivery_address && (
                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiMapPin className="w-4 h-4 text-teal-600" />
                                                            <h4 className="font-semibold text-gray-800">Delivery Address</h4>
                                                        </div>
                                                        <p className="text-sm text-gray-700 whitespace-pre">{delivery.delivery_address}</p>

                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery?.delivery_address)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-1"
                                                        >
                                                            <FiMapPin className="w-3 h-3" />
                                                            Open in Maps
                                                        </a>

                                                    </div>
                                                )}

                                                {/* Special Instructions */}
                                                {delivery?.delivery_notes && (
                                                    <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiAlertCircle className="w-4 h-4 text-amber-600" />
                                                            <h4 className="font-semibold text-amber-900">Delivery Notes</h4>
                                                        </div>
                                                        <p className="text-sm text-amber-800">{delivery.delivery_notes}</p>
                                                    </div>
                                                )}

                                                {/* Action Buttons Based on Delivery Status */}
                                                <div className="space-y-4 pt-4 border-t border-gray-200">
                                                    {(!delivery?.delivery_status || delivery?.delivery_status === 'scheduled') ? (
                                                        // Step 1: Confirm Package Receipt
                                                        <div className="bg-white rounded-lg p-6 border-2 border-teal-300">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                                                                    <FiCheckCircle className="w-6 h-6 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-semibold text-gray-900">Step 1: Confirm Package</h4>
                                                                    <p className="text-sm text-gray-600">Confirm that you have received the {quoteType} package</p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => handleConfirmPackage(quoteType)}
                                                                className="w-full py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-all shadow-sm hover:shadow-md"
                                                            >
                                                                I Have the Package
                                                            </button>
                                                        </div>
                                                    ) : delivery?.delivery_status === 'in_transit' ? (
                                                        // Step 2: Upload Delivery Photos
                                                        <div className="bg-white rounded-lg p-6 border-2 border-blue-300">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                                                                    <FiUpload className="w-6 h-6 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-semibold text-gray-900">Step 2: Upload Delivery Photos</h4>
                                                                    <p className="text-sm text-gray-600">Take photos of the delivered {quoteType} package</p>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-4">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        Upload Photos *
                                                                    </label>
                                                                    <DragDropUploader
                                                                        onFilesChange={handleDeliveryPhotosChange(quoteType)}
                                                                        clearFiles={clearDeliveryPhotos[quoteType]}
                                                                        acceptedTypes=".jpg,.jpeg,.png"
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        Delivery Notes (Optional)
                                                                    </label>
                                                                    <textarea
                                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                                                        value={deliveryNotes[quoteType]}
                                                                        onChange={(e) => setDeliveryNotes(prev => ({
                                                                            ...prev,
                                                                            [quoteType]: e.target.value
                                                                        }))}
                                                                        rows="3"
                                                                        placeholder={`Add any notes about the ${quoteType} delivery...`}
                                                                    />
                                                                </div>

                                                                <button
                                                                    onClick={() => handleUploadDeliveryPhotos(quoteType)}
                                                                    disabled={isSubmittingDelivery || !uploadedDeliveryPhotos[quoteType]?.length}
                                                                    className="w-full py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                                >
                                                                    {isSubmittingDelivery ? (
                                                                        <>
                                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                            Uploading...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiUpload className="w-4 h-4" />
                                                                            Upload Photos
                                                                        </>
                                                                    )}
                                                                </button>

                                                                {/* Show uploaded photos if any */}
                                                                {deliveryPhotos.length > 0 && (
                                                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                                                        <p className="text-sm font-medium text-gray-700 mb-2">
                                                                            Uploaded Photos ({deliveryPhotos.length})
                                                                        </p>
                                                                        <div className="grid grid-cols-3 gap-2 mb-4">
                                                                            {deliveryPhotos.map((photo, index) => {
                                                                                const { data: { publicUrl } } = supabase.storage
                                                                                    .from('delivery-uploads') // Changed from 'job-files'
                                                                                    .getPublicUrl(photo.filename);

                                                                                return (
                                                                                    <div key={index} className="relative aspect-square bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                                                                        <img
                                                                                            src={publicUrl}
                                                                                            alt={`Delivery photo ${index + 1}`}
                                                                                            className="w-full h-full object-contain p-1"
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>

                                                                        {/* Step 3: End Delivery */}
                                                                        <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
                                                                            <div className="flex items-center gap-3 mb-3">
                                                                                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                                                                    <FiCheckCircle className="w-5 h-5 text-white" />
                                                                                </div>
                                                                                <div>
                                                                                    <h4 className="font-semibold text-gray-900">Step 3: Complete Delivery</h4>
                                                                                    <p className="text-sm text-gray-600">Finalize the {quoteType} delivery</p>
                                                                                </div>
                                                                            </div>
                                                                            <button
                                                                                onClick={() => handleEndDelivery(quoteType)}
                                                                                className="w-full py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-all shadow-sm hover:shadow-md"
                                                                            >
                                                                                End Delivery
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : delivery?.delivery_status === 'delivered' ? (
                                                        // Delivery Completed
                                                        <div className="bg-green-50 rounded-lg p-6 border-2 border-green-300">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                                                                    <FiCheckCircle className="w-6 h-6 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-semibold text-green-900">Delivery Completed</h4>
                                                                    <p className="text-sm text-green-700">This {quoteType} delivery has been successfully completed</p>
                                                                </div>
                                                            </div>

                                                            {/* Show delivery photos */}
                                                            {deliveryPhotos.length > 0 && (
                                                                <div className="space-y-3">
                                                                    <p className="text-sm font-medium text-gray-700">
                                                                        Delivery Photos ({deliveryPhotos.length})
                                                                    </p>
                                                                    <div className="grid grid-cols-3 gap-2">
                                                                        {deliveryPhotos.map((photo, index) => {
                                                                            const { data: { publicUrl } } = supabase.storage
                                                                                .from('delivery-uploads')
                                                                                .getPublicUrl(photo.filename);

                                                                            return (
                                                                                <div key={index} className="relative aspect-square">
                                                                                    <img
                                                                                        src={publicUrl}
                                                                                        alt={`Delivery photo ${index + 1}`}
                                                                                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {delivery?.delivered_at && (
                                                                <p className="text-xs text-gray-600 mt-3">
                                                                    Completed on {formatDate(delivery.delivered_at)}
                                                                </p>
                                                            )}

                                                            {delivery?.delivery_notes && (
                                                                <div className="mt-3 p-3 bg-white rounded border border-green-200">
                                                                    <p className="text-xs font-medium text-gray-700 mb-1">Delivery Notes:</p>
                                                                    <p className="text-xs text-gray-600">{delivery.delivery_notes}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Assigned Users Section */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">
                            Assigned Users
                        </h2>

                        <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                            {/* 1. Job Creator Information */}
                            <InfoCard title="Job Creator" icon={FiUser} color="gray">
                                <DetailRow
                                    label="Created By"
                                    value={assignment.created_by_name || "Unknown"}
                                    icon={FiUser}
                                />
                                <DetailRow
                                    label="Role"
                                    value="Admin"
                                    icon={FiTool}
                                />
                                <DetailRow
                                    label="Created On"
                                    value={formatDate(assignment.created_at)}
                                    icon={FiCalendar}
                                />
                            </InfoCard>

                            {/* 2. Assigned Designers */}
                            {(assignment.job_type === 'Design' || assignment.job_type === 'Design & Estimation') && (
                                <InfoCard title="Assigned Designers" icon={FiEdit3} color="blue">
                                    <div className="space-y-4">
                                        {assignment.job_quotes && (
                                            <div className="space-y-3">
                                                {/* Roof Designers */}
                                                {assignment.job_quotes.find(q => q.quote_type === 'roof' && q.is_required) && (
                                                    <div className="bg-blue-50 p-3 rounded-lg">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-sm font-medium text-blue-900">Roof Design</span>
                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                <button
                                                                    onClick={() => openAssignDesignerModal('roof')}
                                                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                                >
                                                                    {assignment.job_designers?.filter(d => d.quote_type === 'roof').length > 0 ? 'Manage' : 'Assign'}
                                                                </button>
                                                            )}
                                                            {hasPermission(userProfile?.role, ["designer"]) &&
                                                                !assignment.job_designers?.some(d => d.quote_type === 'roof') && (
                                                                    <button
                                                                        onClick={() => handleSelfAssignDesigner('roof')}
                                                                        className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                                                    >
                                                                        Assign Self
                                                                    </button>
                                                                )}
                                                        </div>
                                                        {assignment.job_designers?.filter(d => d.quote_type === 'roof').length > 0 ? (
                                                            <div className="space-y-1">
                                                                {assignment.job_designers.filter(d => d.quote_type === 'roof').map((designer) => (
                                                                    <div key={designer.id} className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">
                                                                                {designer.name.charAt(0)}
                                                                            </div>
                                                                            <span className="text-sm text-gray-900">{designer.name}</span>
                                                                            {designer.is_completed && (
                                                                                <FiCheckCircle className="w-4 h-4 text-green-500" />
                                                                            )}
                                                                        </div>
                                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                            <button
                                                                                onClick={() => handleRemoveDesigner('roof', designer.user_id)}
                                                                                className="text-red-500 hover:text-red-700"
                                                                            >
                                                                                <FiX className="w-3 h-3" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-600">No designer assigned</p>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Wall Designers */}
                                                {assignment.job_quotes.find(q => q.quote_type === 'wall' && q.is_required) && (
                                                    <div className="bg-red-50 p-3 rounded-lg">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-sm font-medium text-red-900">Wall Design</span>
                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                <button
                                                                    onClick={() => openAssignDesignerModal('wall')}
                                                                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                                                                >
                                                                    {assignment.job_designers?.filter(d => d.quote_type === 'wall').length > 0 ? 'Manage' : 'Assign'}
                                                                </button>
                                                            )}
                                                            {hasPermission(userProfile?.role, ["designer"]) &&
                                                                !assignment.job_designers?.some(d => d.quote_type === 'wall') && (
                                                                    <button
                                                                        onClick={() => handleSelfAssignDesigner('wall')}
                                                                        className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                                                                    >
                                                                        Assign Self
                                                                    </button>
                                                                )}
                                                        </div>
                                                        {assignment.job_designers?.filter(d => d.quote_type === 'wall').length > 0 ? (
                                                            <div className="space-y-1">
                                                                {assignment.job_designers.filter(d => d.quote_type === 'wall').map((designer) => (
                                                                    <div key={designer.id} className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                                                                                {designer.name.charAt(0)}
                                                                            </div>
                                                                            <span className="text-sm text-gray-900">{designer.name}</span>
                                                                            {designer.is_completed && (
                                                                                <FiCheckCircle className="w-4 h-4 text-green-500" />
                                                                            )}
                                                                        </div>
                                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                            <button
                                                                                onClick={() => handleRemoveDesigner('wall', designer.user_id)}
                                                                                className="text-red-500 hover:text-red-700"
                                                                            >
                                                                                <FiX className="w-3 h-3" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-600">No designer assigned</p>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Floor Designers */}
                                                {assignment.job_quotes.find(q => q.quote_type === 'floor' && q.is_required) && (
                                                    <div className="bg-green-50 p-3 rounded-lg">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-sm font-medium text-green-900">Floor Design</span>
                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                <button
                                                                    onClick={() => openAssignDesignerModal('floor')}
                                                                    className="text-xs text-green-600 hover:text-green-800 font-medium"
                                                                >
                                                                    {assignment.job_designers?.filter(d => d.quote_type === 'floor').length > 0 ? 'Manage' : 'Assign'}
                                                                </button>
                                                            )}
                                                            {hasPermission(userProfile?.role, ["designer"]) &&
                                                                !assignment.job_designers?.some(d => d.quote_type === 'floor') && (
                                                                    <button
                                                                        onClick={() => handleSelfAssignDesigner('floor')}
                                                                        className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                                                                    >
                                                                        Assign Self
                                                                    </button>
                                                                )}
                                                        </div>
                                                        {assignment.job_designers?.filter(d => d.quote_type === 'floor').length > 0 ? (
                                                            <div className="space-y-1">
                                                                {assignment.job_designers.filter(d => d.quote_type === 'floor').map((designer) => (
                                                                    <div key={designer.id} className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs">
                                                                                {designer.name.charAt(0)}
                                                                            </div>
                                                                            <span className="text-sm text-gray-900">{designer.name}</span>
                                                                            {designer.is_completed && (
                                                                                <FiCheckCircle className="w-4 h-4 text-green-500" />
                                                                            )}

                                                                        </div>
                                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                            <button
                                                                                onClick={() => handleRemoveDesigner('floor', designer.user_id)}
                                                                                className="text-red-500 hover:text-red-700"
                                                                            >
                                                                                <FiX className="w-3 h-3" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-600">No designer assigned</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {hasPermission(userProfile?.role, ["estimator"]) === 'estimator' && (
                                            <p className="text-xs text-gray-500 italic">View only - Contact admin to modify designers</p>
                                        )}
                                    </div>
                                </InfoCard>
                            )}

                            {/* 3. Assigned Estimator */}
                            {(assignment.job_type === 'Estimation' || assignment.job_type === 'Design & Estimation') && (
                                <InfoCard title="Assigned Estimator" icon={FiDollarSign} color="green">
                                    <div className="space-y-4">
                                        {assignment.job_estimators?.user_id ? (
                                            <div className="bg-green-50 p-3 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-green-900">Estimator</span>
                                                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                        <button
                                                            onClick={() => handleRemoveEstimator()}
                                                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm">
                                                        {assignment.job_estimators?.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {assignment.job_estimators?.full_name?.trim()
                                                                ? assignment.job_estimators.full_name
                                                                : assignment.job_estimators?.name}
                                                        </span>
                                                        {assignment.job_estimators?.is_completed && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <FiCheckCircle className="w-3 h-3 text-green-500" />
                                                                <span className="text-xs text-green-600">Completed</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <DetailRow
                                                    label="Assigned On"
                                                    value={formatDate(assignment.job_estimators?.assigned_at)}
                                                    icon={FiCalendar}
                                                />
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-sm text-gray-600">No estimator assigned</p>

                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                    <button
                                                        onClick={() => openAssignEstimatorModal()}
                                                        className="w-full py-2 px-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                                                    >
                                                        Assign Estimator
                                                    </button>
                                                )}

                                                {hasPermission(userProfile?.role, ["estimator"]) && (
                                                    <button
                                                        onClick={() => handleSelfAssignEstimator()}
                                                        className="w-full py-2 px-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                                                    >
                                                        Assign Self as Estimator
                                                    </button>
                                                )}

                                                {hasPermission(userProfile?.role, ["designer"]) && (
                                                    <p className="text-xs text-gray-500 italic">Contact admin to assign estimator</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </InfoCard>
                            )}


                            {/* 4. Assigned Drivers - By Quote Type - Visible to admin and driver */}

                            <InfoCard title="Assigned Drivers" icon={FiTruck} color="teal">
                                <div className="space-y-4">
                                    {['roof', 'wall', 'floor', 'timber']
                                        .filter(type => assignment.job_quotes?.some(q => q.quote_type === type && q.is_required))
                                        .map(quoteType => {
                                            const delivery = assignment.job_deliveries?.find(d => d.quote_type === quoteType);
                                            const isCurrentDriver = userProfile?.role === 'driver' && delivery?.assigned_driver_id === userProfile.id;

                                            return (
                                                <div key={quoteType} className={`rounded-lg p-4 border-2 ${quoteType === 'roof' ? 'bg-blue-50 border-blue-200' :
                                                    quoteType === 'wall' ? 'bg-red-50 border-red-200' :
                                                        quoteType === 'timber' ? 'bg-amber-50 border-amber-200' :
                                                            'bg-green-50 border-green-200'
                                                    }`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-6 h-6 rounded flex items-center justify-center ${quoteType === 'roof' ? 'bg-blue-500 text-white' :
                                                                quoteType === 'wall' ? 'bg-red-500 text-white' :
                                                                    quoteType === 'timber' ? 'bg-amber-500 text-white' :
                                                                        'bg-green-500 text-white'
                                                                }`}>
                                                                {quoteType === 'roof' ? <FiHome className="w-3 h-3" /> :
                                                                    quoteType === 'wall' ? <FiLayers className="w-3 h-3" /> :
                                                                        <FiLayers className="w-3 h-3 rotate-90" />}
                                                            </div>
                                                            <span className="text-sm font-semibold text-gray-900 capitalize">
                                                                {quoteType} Delivery
                                                            </span>
                                                        </div>
                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                            <button
                                                                onClick={() => delivery?.assigned_driver_id ? handleRemoveDriver(quoteType) : openAssignDriverModal(quoteType)}
                                                                className={`text-xs font-medium ${delivery?.assigned_driver_id
                                                                    ? 'text-red-600 hover:text-red-800'
                                                                    : 'text-teal-600 hover:text-teal-800'
                                                                    }`}
                                                            >
                                                                {delivery?.assigned_driver_id ? 'Remove' : 'Assign Driver'}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {delivery?.assigned_driver_id ? (
                                                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                                        {delivery.assigned_driver_name?.charAt(0) || 'D'}
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-medium text-gray-900">
                                                                            {delivery.assigned_driver_name || 'Unknown Driver'}
                                                                            {isCurrentDriver && (
                                                                                <span className="ml-2 text-xs text-teal-600">(You)</span>
                                                                            )}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">
                                                                            Assigned {formatDate(delivery.assigned_at)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${delivery?.delivery_status === 'delivered' ? 'bg-green-100 text-green-700' :
                                                                    delivery?.delivery_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                                                                        delivery?.delivery_status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                                                                            'bg-gray-100 text-gray-700'
                                                                    }`}>
                                                                    {delivery?.delivery_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                                </span>
                                                            </div>

                                                            {delivery?.delivery_date && (
                                                                <div className="mt-2 pt-2 border-t border-gray-100">
                                                                    <p className="text-xs text-gray-600">
                                                                        Scheduled: {formatDate(delivery.delivery_date)}
                                                                        {delivery.delivery_time && ` at ${delivery.delivery_time}`}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="bg-white rounded-lg p-3 border border-dashed border-gray-300">
                                                            <p className="text-sm text-gray-500 text-center">No driver assigned</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </InfoCard>

                        </div>


                    </div>

                    {/* Design Management Section */}
                    {(assignment.job_type === "Design" || assignment.job_type === "Design & Estimation") &&
                        hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between relative w-full">
                                    <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-blue-500 pb-2 w-full">
                                        Design Status & Actions
                                    </h2>
                                    <div className="text-sm text-gray-500 absolute right-0">
                                        {assignment.job_quotes?.filter(q => q.is_required).length} sections required
                                    </div>
                                </div>

                                <div className="grid gap-6">
                                    {assignment.job_quotes?.filter(q => q.is_required).map(quote => {
                                        const quoteType = quote.quote_type;
                                        const designers = assignment.job_designers?.filter(d => d.quote_type === quoteType);
                                        const userDesigner = designers?.find(d => d.user_id === userProfile.id);
                                        const isUserAssigned = userProfile?.role === 'designer' && userDesigner;

                                        return (
                                            <div key={quoteType} className="rounded-xl border-2 border-blue-200 bg-blue-50 overflow-hidden transition-all hover:shadow-lg">
                                                {/* Header */}
                                                <div className="px-6 py-4 bg-white border-b border-gray-100">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${quoteType === 'roof' ? 'bg-blue-100 text-blue-600' :
                                                                quoteType === 'wall' ? 'bg-red-100 text-red-600' :
                                                                    'bg-green-100 text-green-600'
                                                                }`}>
                                                                {quoteType === 'roof' ? <FiHome className="w-4 h-4" /> :
                                                                    quoteType === 'wall' ? <FiLayers className="w-4 h-4" /> :
                                                                        <FiLayers className="w-4 h-4 rotate-90" />}
                                                            </div>
                                                            <div>
                                                                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                                                                    {quoteType} Design
                                                                </h3>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${quote.design_status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                                                quote.design_status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-amber-100 text-amber-800'
                                                                }`}>
                                                                {quote.design_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="p-6 space-y-6">
                                                    {/* Assigned Designers */}
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiUser className="w-4 h-4 text-gray-400" />
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {designers?.length > 0 ? "Assigned Designers" : "No Designer Assigned"}
                                                            </span>
                                                        </div>

                                                        {designers && designers.length > 0 ? (
                                                            <div className="space-y-2">
                                                                {designers.map((designer) => (
                                                                    <div className="flex flex-col space-y-3">
                                                                        <div key={designer.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                                            <div className="flex items-center gap-3">
                                                                                <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                                                    {designer.name.charAt(0)}
                                                                                </div>
                                                                                <div>
                                                                                    <p className="font-medium text-gray-900">{designer.name}</p>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        Assigned {formatDate(designer.assigned_at)}
                                                                                    </p>
                                                                                </div>

                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                {designer.is_completed ? (
                                                                                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                                                                        <FiCheckCircle className="w-3 h-3" />
                                                                                        Completed
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                                                                        <FiClock className="w-3 h-3" />
                                                                                        In Progress
                                                                                    </div>
                                                                                )}
                                                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                    <button
                                                                                        onClick={() => handleRemoveDesigner(quoteType, designer.user_id)}
                                                                                        className="text-red-500 hover:text-red-700"
                                                                                    >
                                                                                        <FiX className="w-5 h-5" />
                                                                                    </button>
                                                                                )}
                                                                            </div>

                                                                        </div>
                                                                        <div className="flex flex-col">
                                                                            <div className="flex items-center gap-2 mb-3">
                                                                                <FiPenTool className="rotate-180 w-4 h-4 text-gray-400" />
                                                                                <p className="text-sm font-medium text-gray-700">Finishing Note</p>
                                                                            </div>

                                                                            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm" >
                                                                                <p className={`text-sm ${designer?.finishing_note?.length <= 0 ? "text-gray-500 normal-case" : "text-green-700 italic"}`}>
                                                                                    {designer?.finishing_note || "Not specified any notes."}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-200">
                                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                    <button
                                                                        onClick={() => openAssignDesignerModal(quoteType)}
                                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium w-full h-full py-4"
                                                                    >
                                                                        Assign Designer
                                                                    </button>
                                                                )}
                                                                {hasPermission(userProfile?.role, ["designer"]) && (
                                                                    <button
                                                                        onClick={() => handleSelfAssignDesigner(quoteType)}
                                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium w-full h-full py-4"
                                                                    >
                                                                        Assign Self
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Designer Action Panel - Only for assigned designers who haven't completed */}
                                                    {isUserAssigned && !userDesigner.is_completed && (
                                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
                                                            <div className="flex items-center gap-2 mb-4">
                                                                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                                                    <FiTool className="w-3 h-3 text-white" />
                                                                </div>
                                                                <h4 className="font-semibold text-green-900">Complete Your Design</h4>
                                                            </div>

                                                            <div className="space-y-4">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        Upload Design Files *
                                                                    </label>
                                                                    <div className="bg-white p-4 rounded-lg border-gray-200 hover:border-green-300 transition-colors">
                                                                        <DragDropUploader
                                                                            onFilesChange={handlePdfsChange}
                                                                            clearFiles={clearPdfs}
                                                                            acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        Completion Notes
                                                                    </label>
                                                                    <textarea
                                                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                        value={completionNotes[quoteType] || ''}
                                                                        onChange={(e) => setCompletionNotes(prev => ({
                                                                            ...prev,
                                                                            [quoteType]: e.target.value
                                                                        }))}
                                                                        rows="3"
                                                                        placeholder={`Add any notes about the completed ${quoteType} design work...`}
                                                                    />
                                                                </div>

                                                                <div className="flex justify-end pt-2">
                                                                    <button
                                                                        onClick={() => handleCompleteDesign(quoteType)}
                                                                        disabled={isSubmittingCompletion || !uploadedFiles?.pdfFiles?.length}
                                                                        className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        {isSubmittingCompletion ? (
                                                                            <>
                                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                                Submitting...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FiCheckCircle className="w-4 h-4" />
                                                                                Complete Design
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                    {/* Estimation Management Section */}
                    {(assignment.job_type === "Estimation" || assignment.job_type === "Design & Estimation") &&
                        hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">
                                    Estimation Status & Actions
                                </h2>

                                <div className="rounded-xl border-2 border-purple-200 bg-purple-50 overflow-hidden">
                                    <div className="px-6 py-4 bg-white border-b border-gray-100">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600">
                                                    <FiDollarSign className="w-4 h-4" />
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-900">Estimation</h3>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 space-y-6">
                                        {/* Assigned Estimator */}
                                        {assignment.job_estimators?.user_id ? (
                                            <div>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <FiUser className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-700">Assigned Estimator</span>
                                                </div>
                                                <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                            {assignment.job_estimators.name.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">{assignment.job_estimators.name}</p>
                                                            <p className="text-xs text-gray-500">
                                                                Assigned {formatDate(assignment.job_estimators.assigned_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {assignment.job_estimators.is_completed ? (
                                                            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                                                <FiCheckCircle className="w-3 h-3" />
                                                                Completed
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                                                <FiClock className="w-3 h-3" />
                                                                In Progress
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-200">
                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                    <button
                                                        onClick={openAssignEstimatorModal}
                                                        className="text-xs text-purple-600 hover:text-purple-800 font-medium w-full h-full py-4"
                                                    >
                                                        Assign Estimator
                                                    </button>
                                                )}
                                                {hasPermission(userProfile?.role, ["estimator"]) && (
                                                    <button
                                                        onClick={handleSelfAssignEstimator}
                                                        className="text-xs text-purple-600 hover:text-purple-800 font-medium w-full h-full py-4"
                                                    >
                                                        Assign Self
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {/* Estimator Action Panel */}
                                        {hasPermission(userProfile?.role, ["estimator"]) &&
                                            assignment.job_estimators?.user_id === userProfile.id &&
                                            !assignment.job_estimators?.is_completed && (
                                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl p-6">
                                                    <div className="flex items-center gap-2 mb-4">
                                                        <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                                            <FiDollarSign className="w-3 h-3 text-white" />
                                                        </div>
                                                        <h4 className="font-semibold text-purple-900">Complete Estimation</h4>
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Upload Estimation Files *
                                                            </label>
                                                            <div className="bg-white p-4 rounded-lg border-gray-200 hover:border-purple-300 transition-colors">
                                                                <DragDropUploader
                                                                    onFilesChange={handlePdfsChange}
                                                                    clearFiles={clearPdfs}
                                                                    acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Completion Notes
                                                            </label>
                                                            <textarea
                                                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                value={completionNotes.estimation || ''}
                                                                onChange={(e) => setCompletionNotes(prev => ({
                                                                    ...prev,
                                                                    estimation: e.target.value
                                                                }))}
                                                                rows="3"
                                                                placeholder="Add any notes about the completed estimation..."
                                                            />
                                                        </div>

                                                        <div className="flex justify-end pt-2">
                                                            <button
                                                                onClick={handleCompleteEstimation}
                                                                disabled={isSubmittingCompletion || !uploadedFiles?.pdfFiles?.length}
                                                                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {isSubmittingCompletion ? (
                                                                    <>
                                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                        Submitting...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FiCheckCircle className="w-4 h-4" />
                                                                        Complete Estimation
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        )}

                    {!hasPermission(userProfile?.role, ["driver"]) &&
                        <>
                            {/* Job Specifications */}
                            {assignment.job_quotes?.some(q => q.is_required && ['roof', 'wall', 'floor'].includes(q.quote_type)) && (
                                <div className="space-y-6">
                                    <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">
                                        Job Specifications
                                    </h2>

                                    {/* Roof Specifications */}
                                    {assignment.job_quotes?.find(q => q.quote_type === 'roof' && q.is_required) && (
                                        <InfoCard title="Roof Specifications" icon={FiHome} color="blue">
                                            <DetailRow
                                                label="Roof Type"
                                                value={assignment.roof_type}
                                            />
                                            <DetailRow
                                                label="Roof Truss Centres"
                                                value={assignment.roof_truss_centres}
                                            />
                                        </InfoCard>
                                    )}

                                    {/* Wall Specifications */}
                                    {assignment.job_quotes?.find(q => q.quote_type === 'wall' && q.is_required) && (
                                        <InfoCard title="Wall Specifications" icon={FiLayers} color="red">
                                            <DetailRow
                                                label="Building Type"
                                                value={assignment.is_single_storey ? "Single Storey" : "Double Storey"}
                                            />

                                            {assignment.is_single_storey ? (
                                                // Single Storey - Get from job_floor_specs where floor_level = 'single_storey' or 'ground_floor'
                                                assignment.job_floor_specs?.filter(spec =>
                                                    spec.floor_level === 'single' || spec.floor_level === 'ground'
                                                ).map((spec, index) => (
                                                    <div key={index} className="space-y-2 border-l-4 border-gray-200 pl-4">
                                                        <p className="text-sm font-medium text-gray-900">Single Storey Details</p>
                                                        <DetailRow
                                                            label="Load Bearing Wall Stud Sizes"
                                                            value={spec.load_bearing_wall_stud_sizes}
                                                        />
                                                        <DetailRow
                                                            label="Non Load Bearing Wall Stud Sizes"
                                                            value={spec.non_load_bearing_wall_stud_sizes}
                                                        />
                                                        <DetailRow
                                                            label="Non Load Bearing Wall Stud Centres"
                                                            value={spec.non_load_bearing_wall_stud_centres}
                                                        />
                                                        <DetailRow
                                                            label="Wall Height"
                                                            value={spec.wall_height}
                                                        />
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="space-y-4">
                                                    {/* Ground Floor */}
                                                    {assignment.job_floor_specs?.filter(spec => spec.floor_level === 'ground').map((spec, index) => (
                                                        <div key={index} className="space-y-2 border-l-4 border-blue-200 pl-4">
                                                            <p className="text-sm font-medium text-blue-900">Ground Floor</p>
                                                            <DetailRow
                                                                label="Load Bearing Wall Stud Sizes"
                                                                value={spec.load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Sizes"
                                                                value={spec.non_load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Centres"
                                                                value={spec.non_load_bearing_wall_stud_centres}
                                                            />
                                                            <DetailRow
                                                                label="Wall Height"
                                                                value={spec.wall_height}
                                                            />
                                                        </div>
                                                    ))}

                                                    {/* First Floor */}
                                                    {assignment.job_floor_specs?.filter(spec => spec.floor_level === 'first').map((spec, index) => (
                                                        <div key={index} className="space-y-2 border-l-4 border-green-200 pl-4">
                                                            <p className="text-sm font-medium text-green-900">First Floor</p>
                                                            <DetailRow
                                                                label="Load Bearing Wall Stud Sizes"
                                                                value={spec.load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Sizes"
                                                                value={spec.non_load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Centres"
                                                                value={spec.non_load_bearing_wall_stud_centres}
                                                            />
                                                            <DetailRow
                                                                label="Wall Height"
                                                                value={spec.wall_height}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </InfoCard>
                                    )}

                                    {/* Floor Specifications */}
                                    {assignment.job_quotes?.find(q => q.quote_type === 'floor' && q.is_required) &&
                                        assignment.job_floors && assignment.job_floors.length > 0 && (
                                            <InfoCard title="Floor Specifications" icon={FiLayers} color="green">
                                                <div className="space-y-4">
                                                    {assignment.job_floors.map((floor) => (
                                                        <div key={floor.id} className="border-l-4 border-green-200 pl-4 space-y-2">
                                                            <p className="text-sm font-medium text-green-900">
                                                                Floor {floor.floor_number}
                                                            </p>
                                                            <DetailRow
                                                                label="Floor Truss Size"
                                                                value={floor.floor_truss_size}
                                                            />
                                                            <DetailRow
                                                                label="Flooring Type"
                                                                value={floor.flooring}
                                                            />
                                                            <CheckboxDisplay
                                                                label="Balcony Included"
                                                                isChecked={floor.balcony_included}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </InfoCard>
                                        )}

                                    {/* Site Measure */}
                                    {assignment.site_measure_required && (
                                        <InfoCard title="Site Measure Requirements" icon={FiMapPin} color="yellow">
                                            <div className="flex flex-wrap gap-3">
                                                <CheckboxDisplay
                                                    label="Wall Measurement"
                                                    isChecked={assignment.site_measure_wall}
                                                />
                                                <CheckboxDisplay
                                                    label="Floor Measurement"
                                                    isChecked={assignment.site_measure_floor}
                                                />
                                                <CheckboxDisplay
                                                    label="Truss Measurement"
                                                    isChecked={assignment.site_measure_truss}
                                                />
                                            </div>
                                        </InfoCard>
                                    )}
                                </div>
                            )}
                        </>
                    }


                    {!hasPermission(userProfile?.role, ["driver"]) &&
                        <>
                            {/* Additional Information */}
                            < div className={`grid gap-6 grid-cols-1`}>

                                {/* Additional Details */}
                                <InfoCard title="Additional Information" icon={FiAlertCircle} color="gray">
                                    {assignment.jobType !== "Timber" &&
                                        <CheckboxDisplay
                                            label="Unit Project"
                                            isChecked={assignment?.is_unit}
                                        />
                                    }
                                    {assignment.jobInformation?.isUnit && (
                                        <DetailRow
                                            label="Number of Units"
                                            value={assignment?.unit_count}
                                        />
                                    )}

                                    <CheckboxDisplay
                                        label="Invoiced"
                                        isChecked={assignment.invoiced}
                                    />
                                </InfoCard>



                                {/* Files & Documents with Categorization */}
                                {assignment.job_files && assignment.job_files.length > 0 && (
                                    <InfoCard title="Files & Documents" icon={FiFileText} color="purple">
                                        <div className="space-y-6">
                                            {/* Group files by fileType first, then by quoteType */}
                                            {['initial', 'design', 'estimation', 'timber', 'delivery'].map(fileType => {
                                                const filesOfType = assignment.job_files.filter(file => file.file_type === fileType);
                                                if (filesOfType.length === 0) return null;

                                                return (
                                                    <div key={fileType} className="space-y-4">
                                                        <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                                                            <div className={`w-2 h-2 rounded-full ${fileType === 'initial' ? 'bg-blue-500' :
                                                                fileType === 'design' ? 'bg-green-500' : fileType === 'estimation' ? 'bg-purple-500' : 'bg-red-500'
                                                                }`}></div>
                                                            <h4 className="font-semibold text-gray-800 capitalize">
                                                                {fileType + " Files"}
                                                            </h4>
                                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                                {filesOfType.length} file{filesOfType.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>

                                                        {/* Group by quote type within each file type */}
                                                        <div className="space-y-3">
                                                            {['roof', 'wall', 'floor', 'timber', 'general'].map(quoteType => {
                                                                const quoteFiles = filesOfType.filter(file =>
                                                                    (file.quote_type || 'general') === quoteType
                                                                );
                                                                if (quoteFiles.length === 0) return null;

                                                                return (
                                                                    <div key={quoteType} className="ml-4">
                                                                        {/* Quote type header - only show if not general or if there are multiple quote types */}
                                                                        {(quoteType !== 'general' || ['roof', 'wall', 'floor'].some(type =>
                                                                            filesOfType.some(f => (f.quoteType || 'general') === type)
                                                                        )) && (
                                                                                <div className="flex items-center gap-2 mb-2">
                                                                                    <div className={`w-1.5 h-1.5 rounded-full ${quoteType === 'roof' ? 'bg-blue-400' :
                                                                                        quoteType === 'wall' ? 'bg-red-400' :
                                                                                            quoteType === 'floor' ? 'bg-green-400' :
                                                                                                'bg-gray-400'
                                                                                        }`}></div>
                                                                                    <span className="text-sm font-medium text-gray-700 capitalize">
                                                                                        {quoteType === 'general' ? 'General Files' : `${quoteType} Files`}
                                                                                    </span>
                                                                                </div>
                                                                            )}

                                                                        {/* Files list */}
                                                                        <div className="space-y-2 ml-4">
                                                                            {quoteFiles.map((file, index) => (
                                                                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <FiFileText className="w-4 h-4 text-gray-400" />
                                                                                        <div>
                                                                                            <p className="text-sm font-medium text-gray-900">{file.original_name}</p>
                                                                                            <p className="text-xs text-gray-500">
                                                                                                Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex flex-row gap-2 items-center justify-center content-center">
                                                                                        <button
                                                                                            onClick={() => seeFile(file, assignment.job_files)}
                                                                                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors mr-2"
                                                                                            title="View file"
                                                                                        >
                                                                                            <FiEye className="w-4 h-4" />
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => downloadFile(file)}  // NEW - passing the entire file object
                                                                                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                                                            title="Download file"
                                                                                        >
                                                                                            <FiDownload className="w-4 h-4" />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </InfoCard>
                                )}


                            </div>
                        </>

                    }

                    {/* Notes Section */}
                    {(assignment.job_notes && assignment.job_notes.length > 0) || hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) ? (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">
                                Notes ({assignment.job_notes?.length || 0})
                            </h2>

                            {assignment.job_notes && assignment.job_notes.length > 0 && (
                                <div className="space-y-4">
                                    {assignment.job_notes.map((note) => (
                                        <div key={note.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            {editingNoteId === note.id ? (
                                                <div className="space-y-3">
                                                    <textarea
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                        value={editingNoteContent}
                                                        onChange={(e) => setEditingNoteContent(e.target.value)}
                                                        rows="3"
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleSaveEditNote(note.id)}
                                                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                                                        >
                                                            Save Changes
                                                        </button>
                                                        <button
                                                            onClick={cancelEditNote}
                                                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">
                                                        {note.content}
                                                    </p>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${note.added_by_role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                                                note.added_by_role === 'designer' ? 'bg-green-100 text-green-800' :
                                                                    'bg-purple-100 text-purple-800'
                                                                }`}>
                                                                {note.added_by_name.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{note.added_by_name}</p>
                                                                <p className="text-xs text-gray-500 capitalize">{note.added_by_role}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="text-right">
                                                                <p className="text-xs text-gray-500">{formatDate(note.added_at)}</p>
                                                                {note.is_edited && (
                                                                    <p className="text-xs text-gray-400">Edited {formatDate(note.edited_at)}</p>
                                                                )}
                                                            </div>
                                                            {canUserModifyNote(note) && (
                                                                <div className="flex gap-1 ml-2">
                                                                    <button
                                                                        onClick={() => startEditNote(note.id, note.content)}
                                                                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                                        title="Edit note"
                                                                    >
                                                                        <FiEdit3 className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteNote(note.id)}
                                                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                                        title="Delete note"
                                                                    >
                                                                        <FiX className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add Note Form */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">Add New Note</label>
                                    <textarea
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                        value={newNote}
                                        onChange={(e) => setNewNote(e.target.value)}
                                        placeholder="Add a note to this assignment..."
                                        rows="3"
                                    />
                                    {newNote.trim() && (
                                        <button
                                            onClick={handleAddNote}
                                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                                        >
                                            Add Note
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Designer Assignment Modal */}
                    {showDesignerModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg max-w-md w-full p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Assign Designer for {selectedQuoteType?.charAt(0).toUpperCase() + selectedQuoteType?.slice(1)}
                                    </h3>
                                    <button
                                        onClick={() => setShowDesignerModal(false)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Designer
                                        </label>
                                        <select
                                            value={selectedDesignerId}
                                            onChange={(e) => setSelectedDesignerId(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="">Choose a designer...</option>
                                            {availableDesigners.map(designer => (
                                                <option key={designer.id} value={designer.id}>
                                                    {designer.username || designer.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleAssignDesigner}
                                            disabled={!selectedDesignerId}
                                            className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Assign Designer
                                        </button>
                                        <button
                                            onClick={() => setShowDesignerModal(false)}
                                            className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Estimator Assignment Modal */}
                    {showEstimatorModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg max-w-md w-full p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Assign Estimator</h3>
                                    <button
                                        onClick={() => setShowEstimatorModal(false)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Estimator
                                        </label>
                                        <select
                                            value={selectedEstimatorId}
                                            onChange={(e) => setSelectedEstimatorId(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        >
                                            <option value="">Choose an estimator...</option>
                                            {availableEstimators.map(estimator => (
                                                <option key={estimator.id} value={estimator.id}>
                                                    {estimator.username || estimator.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleAssignEstimator}
                                            disabled={!selectedEstimatorId}
                                            className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Assign Estimator
                                        </button>
                                        <button
                                            onClick={() => setShowEstimatorModal(false)}
                                            className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Driver Assignment Modal */}
                    {showDriverModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg max-w-md w-full p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Assign Driver for {selectedQuoteTypeForDriver?.charAt(0).toUpperCase() + selectedQuoteTypeForDriver?.slice(1)} Delivery
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setShowDriverModal(false);
                                            setSelectedQuoteTypeForDriver(null);
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Driver
                                        </label>
                                        <select
                                            value={selectedDriverId}
                                            onChange={(e) => setSelectedDriverId(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        >
                                            <option value="">Choose a driver...</option>
                                            {availableDrivers.map(driver => (
                                                <option key={driver.id} value={`${driver.id}_${driver.username || driver.full_name}`}>
                                                    {driver.username || driver.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleAssignDriver}
                                            disabled={!selectedDriverId}
                                            className="flex-1 py-2 px-4 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Assign Driver
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowDriverModal(false);
                                                setSelectedQuoteTypeForDriver(null);
                                                setSelectedDriverId('');
                                            }}
                                            className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!hasPermission(userProfile?.role, ["driver"]) &&
                        <>
                            {/* Action Logs */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">
                                        Activity Log
                                    </h2>
                                    <button
                                        onClick={() => setShowLogs(!showLogs)}
                                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        {showLogs ? 'Hide' : 'Show'} Logs ({actionLogs.length})
                                    </button>
                                </div>

                                {showLogs && (
                                    <div className="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                                        {actionLogs.length === 0 ? (
                                            <p className="text-gray-600 text-sm">No activity logs yet.</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {actionLogs.map((log) => (
                                                    <div key={log.id} className="bg-white p-3 rounded-lg border border-gray-200">
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{log.action}</p>
                                                                <p className="text-sm text-gray-600">{log.details}</p>
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    by {log.user_name} ({log.user_role})
                                                                </p>
                                                            </div>
                                                            <span className="text-xs text-gray-400">
                                                                {formatDate(log.timestamp)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    }

                    {/* Actions */}
                    <div className="flex gap-3 pt-6 border-t border-gray-200">
                        {hasPermission(userProfile?.role, ["super_admin", "admin", "estimator", "designer"]) && (
                            <button
                                onClick={() => navigate(`/${companySlug}/assignment/${jobId}/edit`)}
                                className="flex-1 sm:flex-none px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                            >
                                Edit Assignment
                            </button>
                        )}
                        <button
                            onClick={() => navigate(`/${companySlug}/assignments`)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                        >
                            Back to List
                        </button>
                    </div>
                </div>
            </div >

            {/* File Viewer Modal */}
            {
                fileViewerOpen && selectedFile && (
                    <FileViewer
                        file={selectedFile}
                        filename={viewerFiles[selectedFileIndex].filename}
                        originalName={viewerFiles[selectedFileIndex].original_name}
                        files={viewerFiles}
                        currentIndex={selectedFileIndex}
                        onNavigate={(newIndex) => {
                            setSelectedFileIndex(newIndex);
                            setSelectedFile(viewerFiles[newIndex]);
                        }}
                        onClose={() => setFileViewerOpen(false)}
                        jobId={assignment.id}
                        company={companySlug}
                    />
                )
            }

        </>
    );
};

export default SingleAssignment;